import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Facebook from '@/assets/socialMedia/Facebook.svg?react'
import Instagram from '@/assets/socialMedia/Instagram.svg?react'
import Linkedln from '@/assets/socialMedia/LinkedIn.svg?react'
import Pinterest from '@/assets/socialMedia/Pinterest.svg?react'
import Reddit from '@/assets/socialMedia/Reddit.svg?react'
import TikTok from '@/assets/socialMedia/TikTok.svg?react'
import X from '@/assets/socialMedia/X.svg?react'
import { selectUserInfo } from '@/store/api/Users/Users.selector'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'

import { LoadSocialMediaSheet } from './LoadSocialMediaSheet'

export interface SocialMediaLink {
  url: string
  type:
    | 'x'
    | 'tiktok'
    | 'linkedin'
    | 'facebook'
    | 'instagram'
    | 'twitter'
    | 'pinterest'
    | 'reddit'
}

export const SocialMediaAccounts = () => {
  const socialMediaData = useSelector(selectUserInfo).socialMedias
  const { t } = useTranslation()
  const socialMediaArray: SocialMediaLink[] = useMemo(() => {
    const safeSocialMediaData = socialMediaData || []

    return safeSocialMediaData
      .filter(
        (socialMedia) =>
          socialMedia.profileLink && socialMedia.profileLink.trim() !== '',
      )
      .map((socialMedia) => ({
        url: socialMedia.profileLink,
        type: socialMedia.name.toLowerCase() as SocialMediaLink['type'],
      }))
  }, [socialMediaData])

  // Definir todas las redes sociales disponibles
  const allSocialMediaTypes = useMemo(
    () => [
      { logo: Facebook, type: 'facebook' as const },
      { logo: Instagram, type: 'instagram' as const },
      { logo: Linkedln, type: 'linkedin' as const },
      { logo: TikTok, type: 'tiktok' as const },
      { logo: X, type: 'twitter' as const },
      { logo: Pinterest, type: 'pinterest' as const },
      { logo: Reddit, type: 'reddit' as const },
    ],
    [],
  )

  const hasSocialMedia = (
    socialMedia:
      | 'x'
      | 'tiktok'
      | 'linkedin'
      | 'facebook'
      | 'instagram'
      | 'twitter'
      | 'pinterest'
      | 'reddit',
  ) => socialMediaArray.some((item) => item.type === socialMedia)

  const socialMediaIcons = useMemo(() => {
    const Wrapper = ({
      children,
      isLoaded,
    }: {
      children: React.ReactNode
      isLoaded: boolean
    }) => (
      <div
        className={`p-4 border rounded-full ${
          isLoaded
            ? 'bg-primaryColor30 border-primary30'
            : 'border-disabled border-dashed'
        }`}
      >
        {children}
      </div>
    )

    const loadedSocialMedia = allSocialMediaTypes.filter((socialMediaType) =>
      hasSocialMedia(socialMediaType.type),
    )
    const unloadedSocialMedia = allSocialMediaTypes.filter(
      (socialMediaType) => !hasSocialMedia(socialMediaType.type),
    )

    const sortedSocialMediaTypes = [
      ...loadedSocialMedia,
      ...unloadedSocialMedia,
    ]

    return sortedSocialMediaTypes.map((socialMediaType) => {
      const isLoaded = hasSocialMedia(socialMediaType.type)

      return (
        <Wrapper key={socialMediaType.type} isLoaded={isLoaded}>
          <socialMediaType.logo
            color={
              isLoaded
                ? SocialMirrorPalette.primary
                : SocialMirrorPalette.neutralColor30
            }
          />
        </Wrapper>
      )
    })
  }, [socialMediaArray, allSocialMediaTypes])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <p className="text-neutralColor text-xl font-medium">
            {t('setup.socialMediaProfiles.addProfilesTitle')}
          </p>
          <p className="text-neutralColor80 text-base font-normal">
            {t('setup.socialMediaProfiles.addProfilesDescription')}
          </p>
        </div>

        <LoadSocialMediaSheet />
      </div>
      <div className="flex gap-3 max-md:flex-wrap">{socialMediaIcons}</div>
    </div>
  )
}
